// Spell casting and spell effects: the wire formats.
// The server decides everything; clients only send intents (cast, effect
// done, peek) and draw what the server says they may see.
import { z } from "zod";
import { CardSchema } from "./cards";
import { MAX_OVERCHARGE_TOTAL } from "./constants";

/** Showdown stays on screen this long; spells still work during it. */
export const SHOWDOWN_CAST_SECONDS = 10;
/** Flash charges this long before it goes off. */
export const FLASH_CHARGE_SECONDS = 4;
/** Flash blinds (white-out) fully this long after it goes off... */
export const FLASH_FULL_SECONDS = 7;
/** ...then fades back and is completely gone this long after it went off. */
export const FLASH_TOTAL_SECONDS = 10;
/** Disintegrate strikes its target this long after it is assembled (HandMagic.disintegrate). */
export const DISINTEGRATE_DELAY_SECONDS = 5;
/** Blindness lands on its target this long after it is assembled (HandMagic.windups). */
export const BLIND_DELAY_SECONDS = 4;
/** Banishment lands on its target this long after it is assembled (HandMagic.windups). */
export const BANISH_DELAY_SECONDS = 4;
/** Blizzard starts up this long before it goes off (TableMagic.blizzardAt)... */
export const BLIZZARD_STARTUP_SECONDS = 4;
/** ...then freezes everyone it hits (a "chill" effect, spellId "blizzard") this long. */
export const BLIZZARD_FREEZE_SECONDS = 5;
/** Meteor Swarm: the meteors fall this long before they hit (TableMagic.meteorAt). */
export const METEOR_STARTUP_SECONDS = 4;
/** Chill Ray freezes its target (a "chill" effect: no spellbook, no peeking) this long. */
export const CHILL_RAY_FREEZE_SECONDS = 5;
/** Hallucination wobbles its target's screen this long. */
export const HALLUCINATION_SECONDS = 30;
/** Arcane Lock: skill checks needed to pick the lock (also sent as effect data.checks). */
export const ARCANE_LOCK_CHECKS = 3;
/** Before the game starts, seated players may practise spells (free, no effect) this often. */
export const PRACTICE_CAST_COOLDOWN_MS = 1500;
/** Control Weather: the thundercloud hangs over the table this long (TableMagic.weatherUntil). */
export const CONTROL_WEATHER_SECONDS = 60;
/** Control Weather: every this many seconds each player may be struck... */
export const LIGHTNING_EVERY_SECONDS = 6;
/** ...with this chance (1 in 5)... */
export const LIGHTNING_CHANCE = 0.2;
/** ...for this share of their health (current + temporary, rounded down, at least 1). */
export const LIGHTNING_HEALTH_SHARE = 0.1;
/** Chain Lightning: damage of each bolt. */
export const CHAIN_LIGHTNING_DAMAGE = 25;
/** Chain Lightning: the hit player has this long to throw the free recast (MyMagic.chainRecast). */
export const CHAIN_RECAST_SECONDS = 20;
/** Bonk: damage when it goes off. */
export const BONK_DAMAGE = 15;
/** Boom: damage to the hole card's owner each time they flip their hole cards face up... */
export const BOOM_OWNER_DAMAGE = 20;
/** ...and to every other dealt player. */
export const BOOM_OTHERS_DAMAGE = 15;
/** Overcharge: overcharge juice gained per 1 health spent. */
export const OVERCHARGE_PER_HEALTH = 2;
/** False Life: juice spent per 1 temporary health. */
export const FALSE_LIFE_JUICE_PER_HEALTH = 2;

// ---------- references ----------

export const CardRefSchema = z.discriminatedUnion("zone", [
  z.object({ zone: z.literal("community"), index: z.number().int().min(0).max(4) }),
  z.object({ zone: z.literal("hole"), userId: z.string().min(1).max(64), index: z.number().int().min(0).max(1) }),
]);
export type CardRef = z.infer<typeof CardRefSchema>;

/** What a spell is aimed at. "pot" = the whole table (area spells: the client sends it for a click anywhere on the table). */
export const TargetRefSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("player"), userId: z.string().min(1).max(64) }),
  z.object({ kind: z.literal("card"), ref: CardRefSchema }),
  z.object({ kind: z.literal("pot") }),
  z.object({ kind: z.literal("spell"), userId: z.string().min(1).max(64), spellId: z.string().min(1).max(40) }),
]);
export type TargetRef = z.infer<typeof TargetRefSchema>;

/** A player or a card (cast records, card keys). */
export const ThingRefSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("player"), userId: z.string().min(1).max(64) }),
  z.object({ kind: z.literal("card"), ref: CardRefSchema }),
]);
export type ThingRef = z.infer<typeof ThingRefSchema>;

/** Stable string key for a thing. */
export function thingKey(t: ThingRef): string {
  if (t.kind === "player") return `player:${t.userId}`;
  return t.ref.zone === "community" ? `card:c:${t.ref.index}` : `card:h:${t.ref.userId}:${t.ref.index}`;
}

export function cardKey(ref: CardRef): string {
  return thingKey({ kind: "card", ref });
}

// ---------- client -> server ----------

export const CastBodySchema = z.object({
  spellId: z.string().min(1).max(40),
  /** In order: main target first (Blight: drained player, then who receives). */
  targets: z.array(TargetRefSchema).max(2),
  /** False Life (false_life): juice to spend (2 juice = 1 temporary health). Overcharge: health to spend (1 health = 2 juice). */
  amount: z.number().int().positive().max(200).optional(),
  /** Card Illusion: the card it becomes. */
  card: CardSchema.optional(),
  /** Chain Lightning: this is the free recast (MyMagic.chainRecast), not a new cast. */
  recast: z.boolean().optional(),
});
export type CastBody = z.infer<typeof CastBodySchema>;

// ---------- server -> client: views ----------

export const EffectKindSchema = z.enum([
  "spider",
  "lock",
  "banish",
  "barrier",
  "blind",
  "chill",
  "confusion",
  "counter",
  "fart",
  "flash",
  "hallucination",
  "ignite",
  "insect", // lasts across hands until the target assembles Counterspell, Barrier or Cleanse on themselves
  "grease", // takes 25% more spell damage (rest of the hand, or until Cleanse)
  "paint",
  "stoneskin",
  "silence",
]);
export type EffectKind = z.infer<typeof EffectKindSchema>;

/** An effect on the viewer. `needsDone` = the viewer must finish a minigame and send effectDone. */
export const EffectViewSchema = z.object({
  id: z.string(),
  kind: EffectKindSchema,
  spellId: z.string(),
  /** Epoch ms (server clock) when it ends; null = rest of the hand / until done. */
  until: z.number().int().nullable(),
  needsDone: z.boolean(),
  /** Kind-specific numbers: clicks needed, charges left, blocked spell, etc. */
  data: z.record(z.string(), z.union([z.number(), z.string(), z.boolean(), z.null()])),
});
export type EffectView = z.infer<typeof EffectViewSchema>;

export const KnownCardSchema = z.object({
  ref: CardRefSchema,
  /** The card (never null now: Blur was removed; kept nullable for older servers). */
  card: CardSchema.nullable(),
  color: z.enum(["red", "black"]),
});

export const MyMagicSchema = z.object({
  health: z.number().int(),
  mana: z.number().int(),
  /** Overcharge juice (up to MAX_OVERCHARGE_TOTAL: the Overcharge spell fills 49, Blight can add more). */
  overcharge: z.number().int().min(0).max(MAX_OVERCHARGE_TOTAL),
  /** Temporary health (False Life, Blight above 100), lost first. */
  tempHealth: z.number().int(),
  /** Casts used this hand per spell id. */
  casts: z.record(z.string(), z.number().int()),
  effects: z.array(EffectViewSchema),
  /** Cards this viewer knows through spells (Arcane Eye). */
  knownCards: z.array(KnownCardSchema),
  /** Can the viewer cast right now (timing + nothing stopping them)? The reason when not. */
  canCast: z.boolean(),
  cantCastReason: z.string().nullable(),
  /** Spells blocked by a spider (Arachnid). */
  blockedSpells: z.array(z.string()),
  peeking: z.boolean(),
  /**
   * Chain Lightning hit you: you must throw the free recast ({t:"cast", spellId:"chain_lightning",
   * recast:true}) at any player in the hand but yourself (`from`, the original caster, included) before you can
   * cast or act. Forfeited at `expiresAt`.
   */
  chainRecast: z.object({ from: z.string(), expiresAt: z.number().int() }).nullable().optional(),
});
export type MyMagic = z.infer<typeof MyMagicSchema>;

export const TableMagicSchema = z.object({
  /** null for spectators. */
  me: MyMagicSchema.nullable(),
  /** Public marks on players: "blighted". */
  marks: z.record(z.string(), z.array(z.string())),
  /**
   * Visible marks on cards: "shimmer" (Card Illusion / Fake Out look the same);
   * "trap" only for its caster: one of the viewer's own secret traps (`spellId`: alarm, delayed_fireball,
   * vampire, bonk, boom).
   */
  cardMarks: z.array(z.object({ ref: CardRefSchema, kind: z.enum(["shimmer", "trap"]), spellId: z.string().optional() })),
  /** Control Weather: the thundercloud is up until this time; null when none. */
  weatherUntil: z.number().int().nullable().optional(),
  /** Flash going off at this time (charge running). */
  flashAt: z.number().int().nullable(),
  /** False Alarm's harmless flashbang going off at this time (charges exactly like Flash); null when none. */
  falseAlarmAt: z.number().int().nullable().optional(),
  /** Blizzard going off at this time (4-second startup running); null when none. */
  blizzardAt: z.number().int().nullable().optional(),
  /** Meteor Swarm hitting at this time (4-second startup running); null when none. */
  meteorAt: z.number().int().nullable().optional(),
  /** Showdown: spells are open until this time, then the result is final. */
  finalAt: z.number().int().nullable(),
});
export type TableMagic = z.infer<typeof TableMagicSchema>;

// ---------- server -> client: events (animations / notices) ----------

export const FxSchema = z.discriminatedUnion("k", [
  /** A spell was cast by `from` (always set now; null only from older servers). */
  z.object({
    k: z.literal("cast"),
    spellId: z.string(),
    from: z.string().nullable(),
    /** Chain Lightning's free recast (cost 0). */
    recast: z.boolean().optional(),
    targets: z.array(TargetRefSchema),
    /** A practice cast before the game starts: shown to everyone, no cost, no effect. */
    practice: z.boolean().optional(),
    /**
     * Play the cast animation but do not list it in the actions console
     * (Flash / Disintegrate are seen by everyone; only the caster and, for
     * Disintegrate on a player, its target get the normal cast fx).
     */
    quiet: z.boolean().optional(),
  }),
  z.object({ k: z.literal("damage"), userId: z.string(), amount: z.number().int() }),
  z.object({ k: z.literal("heal"), userId: z.string(), amount: z.number().int() }),
  z.object({ k: z.literal("blocked"), userId: z.string(), by: z.enum(["barrier", "counter", "stoneskin"]) }),
  z.object({ k: z.literal("reflected"), spellId: z.string(), userId: z.string() }),
  /** An Alarm on card `ref` rang, or a False Alarm on it (the same fx: it never says who). */
  z.object({ k: z.literal("alarm"), ref: CardRefSchema }),
  z.object({ k: z.literal("knockout"), userId: z.string() }),
  z.object({ k: z.literal("trap"), trap: z.enum(["fireball", "vampire"]), userId: z.string() }),
  z.object({ k: z.literal("cardChanged"), ref: CardRefSchema }),
  /** A Bonk on card `ref` went off on `userId` ("blocked" / "damage" follow). Sent to everyone. */
  z.object({ k: z.literal("bonk"), userId: z.string(), ref: CardRefSchema }),
  /**
   * A Boom on hole card `ref` exploded as its owner flipped their hole cards face up (owner BOOM_OWNER_DAMAGE,
   * everyone else BOOM_OTHERS_DAMAGE; per-player "blocked" / "damage" follow). Sent to everyone.
   */
  z.object({ k: z.literal("boom"), ref: CardRefSchema }),
  /** Control Weather: the thundercloud is up until `until` (on), or gone (on: false, until: null). Sent to everyone. */
  z.object({ k: z.literal("weather"), on: z.boolean(), until: z.number().int().nullable() }),
  /**
   * Control Weather: lightning strikes `userId` for `damage` (before Stoneskin / Grease). `blocked`: their
   * Barrier stopped it. `reflectedTo`: their Counterspell sent it to the cloud's caster. Sent to everyone.
   */
  z.object({ k: z.literal("lightning"), userId: z.string(), damage: z.number().int(), blocked: z.boolean().optional(), reflectedTo: z.string().optional() }),
  /**
   * Treasure Finder: these players (still in the hand) have the best hand right now. A Fool's Treasure Finder
   * (spell id "suggestion") sends exactly the same fx; only its caster gets `fake: true`.
   */
  z.object({ k: z.literal("treasure"), userIds: z.array(z.string()), fake: z.boolean().optional() }),
  /** A Meteor Swarm hit (damage follows; `wiped`: the community cards were re-dealt). */
  z.object({ k: z.literal("meteor"), wiped: z.boolean().optional() }),
  /**
   * A Flash went off: everyone at the table is blinded and deafened, except `blocked` (stopped by Barrier).
   * `fake`: False Alarm's harmless flashbang: play the same animation, but nobody is blinded or deafened.
   */
  z.object({ k: z.literal("flash"), blocked: z.array(z.string()).optional(), fake: z.boolean().optional() }),
  /**
   * A Disintegrate strikes `target` (DISINTEGRATE_DELAY_SECONDS after its cast fx; "blocked",
   * "damage", "cardChanged" etc. follow). `fizzled`: the target was gone, nothing happens.
   */
  z.object({ k: z.literal("disintegrate"), target: TargetRefSchema, fizzled: z.boolean().optional() }),
  /**
   * A wind-up spell (Blindness, Banishment) lands on `userId`, BLIND_DELAY_SECONDS /
   * BANISH_DELAY_SECONDS after its cast fx ("blocked" / "reflected" may follow; when
   * reflected by Counterspell the effect lands on the caster instead).
   */
  z.object({ k: z.literal("landed"), spellId: z.string(), userId: z.string() }),
  /**
   * A wind-up spell aimed at `userId` will not land: stopped by Cleanse on themselves during
   * the wind-up, or the target was gone / it was their turn (Banishment) when it would land.
   */
  z.object({ k: z.literal("fizzled"), spellId: z.string(), userId: z.string() }),
  /** A Blizzard went off (damage and "chill" freeze effects follow). */
  z.object({ k: z.literal("blizzard") }),
  /**
   * Lingering effects were removed from `userId`: "insect" (Counterspell, Barrier, Cleanse, Dung Fling);
   * "grease", "chill" (freeze) by Cleanse on yourself.
   */
  z.object({ k: z.literal("cleansed"), userId: z.string(), kinds: z.array(z.string()) }),
  z.object({ k: z.literal("notice"), text: z.string() }),
  /**
   * `userId` played a secret trap spell (Alarm, Arcane Eye, Bonk, Boom, Delayed Fireball, Vampire): sent to
   * everyone but the caster; it never says which trap or which card ("<name> played a trap spell...").
   */
  z.object({ k: z.literal("trapPlayed"), userId: z.string() }),
  /** Charm flipped `userId`'s hole cards: `faceUp` = the side they are on now. Sent to everyone. */
  z.object({ k: z.literal("charmed"), userId: z.string(), faceUp: z.boolean() }),
]);
export type Fx = z.infer<typeof FxSchema>;
